export const ROUTES = {
  // Authenticated admin top-level
  home: '/home',
  cohorts: '/cohorts',
  // D.3.3 at-risk dashboard. Reached via per-cohort AtRiskPanel "View all"
  // links and per-row drilldowns, plus deep-link query params
  // (?cohortId=...&ruleKey=...). Capability shared with the EWS Tier 2
  // dashboard (matrix line 54).
  atRisk: '/at-risk',
  // Access management — admin list + invite + assignment surfaces. Visible
  // only to ORG_ADMIN per .specify/foundations/admin-permission-matrix.md.
  accessAdmins: '/access/admins',
  accessAdminsInvite: '/access/admins/invite',
  learners: '/learners',
  goals: '/goals',
  // D.FOUND.1 - DoW resource catalog admin authoring surface. URL value
  // updated 2026-06-15 per DoD → DoW rename (route-key NAMES kept as-is per
  // the Level 2 scope: code identifiers stay, only user-visible strings + URL
  // path values changed).
  dodResources: '/dow-resources',
  dodResourcesNew: '/dow-resources/new',
  dodResourceDetail: (id: string) => `/dow-resources/${id}`,
  reports: '/reports',
  // D.3.5.U sub-routes under /reports — capability inherited via prefix match
  // in route-capability.ts. URL convention is flat; the (app) route group is
  // an organizational segment that doesn't appear in the URL.
  reportsBuild: '/reports/build',
  // In-app Tier 1 results view (sync run -> accessible table + chart +
  // client-side CSV). Dynamic segment `/reports/view/:templateId`; capability
  // inherited via the /reports prefix match in route-capability.ts. Modeled
  // as a function helper (like the apps/web parametrized routes) so the
  // routes-coverage test probes the `[templateId]` dynamic-segment directory
  // rather than a non-existent flat `/reports/view/page.tsx`.
  reportsView: (templateId: string) => `/reports/view/${templateId}`,
  // D.3.5.R.3 - preview-first ARIA report building. Static prefixes drive the
  // route-capability map (first-match-prefix); the dynamic helpers build the
  // per-id URLs the preview navigation + saved-report destination push to.
  reportsPreviewPrefix: '/reports/preview',
  reportsPreview: (proposalId: string) => `/reports/preview/${proposalId}`,
  reportsDefinitionsPrefix: '/reports/definitions',
  reportsDefinition: (definitionId: string) => `/reports/definitions/${definitionId}`,
  reportsRun: (runId: string) => `/reports/runs/${runId}`,
  // D.3.5.R.4 - Contract Reports page (the live pws_aligned templates, surfaced
  // under a plain-English "Contract Reports" heading). Capability inherited via
  // the /reports prefix match in route-capability.ts (reports.read_tier1, all
  // five roles); it shares no prefix with reportsBuild/Preview/Definitions so
  // first-match ordering is unaffected.
  reportsContractReports: '/reports/contract-reports',
  // Phase DP - the Generator page (cadence + open-period slot form) reached
  // from the library shelf's Generate affordances (with ?kind/periodStart/
  // periodEnd prefill) and the library header. STATIC segment, so Next
  // routes it ahead of the [reportId] dynamic sibling. Capability inherited
  // via the /reports prefix match (reports.read_tier1); the generate ACTION
  // is gated on briefing.publish in the UI and re-gated server-side.
  reportsContractReportGenerate: '/reports/contract-reports/generate',
  // D.3.12 (CR-4) - contract-report detail (narrative + delta strip + attached
  // run panels + publisher edit). Dynamic child of /reports/contract-reports
  // ([reportId]); capability inherited via the /reports prefix match
  // (reports.read_tier1) like every other /reports child; draft visibility is
  // enforced server-side (a non-publisher 404s on a draft id).
  reportsContractReport: (reportId: string) => `/reports/contract-reports/${reportId}`,
  // D.3.5.R Phase II - per-section "all reports" pages reached from each landing
  // section's "More" link. All inherit reports.read_tier1 via the /reports
  // prefix match (no ROUTE_CAPABILITY_MAP entry); none share a prefix with
  // reportsBuild/Preview/Definitions so first-match ordering is unaffected.
  reportsEngagement: '/reports/engagement',
  // D.3.5.R2.7 - Satisfaction per-category "more" page. Inherits
  // reports.read_tier1 via the /reports prefix match (no ROUTE_CAPABILITY_MAP
  // entry); shares no prefix with reportsBuild/Preview/Definitions so
  // first-match ordering is unaffected.
  reportsSatisfaction: '/reports/satisfaction',
  reportsProgression: '/reports/progression',
  reportsReadiness: '/reports/readiness',
  reportsMastery: '/reports/mastery',
  reportsCustom: '/reports/custom',
  // Read-only reference / data-dictionary page. Inherits reports.read_tier1 via
  // the /reports prefix match (no ROUTE_CAPABILITY_MAP entry); shares no prefix
  // with reportsBuild/Preview/Definitions so first-match ordering is unaffected.
  reportsReference: '/reports/reference',
  reportsOutOfCatalogNew: '/reports/out-of-catalog/new',
  training: '/training',
  aria: '/aria',
  // C.3.3 — ARIA knowledge base admin surface. Capability gating server-side
  // (kb_admin.upload for the route; the page itself is reachable by any role
  // that holds aria.use, who then sees a view-only state).
  ariaKb: '/aria/kb',
  notifications: '/notifications',
  // C.2.7s tutor response-time health surface; ORG_ADMIN-only per
  // .specify/foundations/admin-permission-matrix.md (added in this PR).
  health: '/health',
  // B-12 tutoring CRM queue. TUTOR + ORG_ADMIN only (tutoring.read). The
  // request-detail route lands with the A2 CRM build.
  tutoring: '/tutoring',
  // B-12 A2 tutoring request detail. Dynamic segment `/tutoring/:requestId`;
  // capability inherited via the /tutoring prefix match in route-capability.ts
  // (modeled as a function helper like reportsDefinition / dodResourceDetail).
  tutoringDetail: (requestId: string) => `/tutoring/${requestId}`,
  settings: '/settings',
  settingsOrg: '/settings/org',
  settingsSecurityEnrollMfa: '/settings/security/enroll-mfa',
  settingsSecurityBackupCodes: '/settings/security/backup-codes',
  // PWS-COMPLIANCE-9 — admin community moderation surface.
  communityModeration: '/community/moderation',
  // A16 (VP QA Wave 3) — admin content-flag triage queue. Surfaces learner
  // "Report a problem" flags for triage; gated on community:moderate (the
  // nearest existing "resolve reports" capability, ORG_ADMIN-only). ADMIN-SIDE
  // ONLY — not wired into the Builder correction pipeline (BLD.CORR.1).
  contentFlags: '/content-flags',
  // Self-serve Help & Troubleshooting. No capability requirement — allow-listed
  // in decideGuard so any authenticated admin/COR can reach it. Intentionally
  // NOT in ADMIN_APP_ROUTES / nav; surfaced via the footer and a Settings tile
  // so it stays unobtrusive. The routes-coverage test requires a page.tsx.
  help: '/help',
  // COR-only. Plural matches the D.3.5.E backend at /api/v1/cor/briefings;
  // the previous singular `/briefing` was a D.1.2 fixture-stub placeholder
  // reserved for D.3.5.U to fill in.
  briefings: '/briefings',
  // COR-only full report-of-record view, opened from a briefing card's
  // "View full report". Dynamic child of /briefings ([briefingId]).
  briefingDetail: (id: string) => `/briefings/${id}`,
  // D.3.5.B — admin-pool briefing authoring (publish a status report to the
  // COR). Gated by `briefing.publish` (ORG_ADMIN + PROGRAM_MANAGER); NOT in
  // the COR-only (cor) group — this is the admin compose side.
  briefingsAdmin: '/briefings-admin',
  // Public auth
  signIn: '/signin',
  signInTotp: '/signin/totp',
  signInBackupCode: '/signin/backup-code',
  accessHelp: '/access-help',
  // 0.D.1.1 admin-invite landing page (auth-onboarding.md Surface 4.8).
  // Token-bearing public route; unauthenticated; lands invitees into the
  // resolve → set-password → enroll-mfa wizard.
  acceptInvite: '/admin/accept-invite',
} as const;

export const ADMIN_APP_ROUTES = [
  ROUTES.home,
  ROUTES.cohorts,
  ROUTES.atRisk,
  ROUTES.accessAdmins,
  ROUTES.accessAdminsInvite,
  ROUTES.learners,
  ROUTES.goals,
  ROUTES.dodResources,
  ROUTES.reports,
  ROUTES.training,
  ROUTES.aria,
  ROUTES.tutoring,
  ROUTES.notifications,
  ROUTES.settings,
] as const;

export const COR_APP_ROUTES = [ROUTES.briefings, ROUTES.notifications] as const;

export const AUTH_ROUTES = [
  ROUTES.signIn,
  ROUTES.signInTotp,
  ROUTES.signInBackupCode,
  ROUTES.accessHelp,
  ROUTES.acceptInvite,
] as const;

export type AdminAppRoute = (typeof ADMIN_APP_ROUTES)[number];
export type CORAppRoute = (typeof COR_APP_ROUTES)[number];
export type AuthRoute = (typeof AUTH_ROUTES)[number];

const SAFE_REDIRECT_REGEX = /^\/(?!\/)[a-zA-Z0-9_\-/]*$/;

export function sanitizeRedirectAfter(value: string | null | undefined): string {
  if (!value) return ROUTES.home;
  return SAFE_REDIRECT_REGEX.test(value) ? value : ROUTES.home;
}
